/* ══════════════════════════════════════════════════════════════════
   Was eine Meldung in der Glocke sagt (M-01, Durchgang 03.10.2026).

   Zwei Dinge standen in alten Meldungen „Protokoll vom Telefon
   abgewiesen" falsch da, auch nachdem D-25 die neuen gefüllt hatte:

   1. **Platzhalter ohne Wert** — „{maschine} hat einen offenen Defekt …
      vom {datum}". Die Werte kamen vor D-25 nicht mit der Meldung; an der
      Meldung steht auch keine Belegung, aus der man sie nachladen könnte.
      Was fehlt, wird darum **neutral ersetzt** („…"), statt die Klammer
      stehen zu lassen. Gespeicherte Meldungen bleiben, wie sie sind.
   2. **Der Kunde doppelt** — die Kennung vom Telefon trägt ihn schon
      („AB-22 · PB-Bau Nord GmbH"), die API setzt ihn noch einmal dazu.
      Jeder Teil steht jetzt einmal.
   ══════════════════════════════════════════════════════════════════ */

/** Die Werte zum Satz eines abgewiesenen Protokolls (`grundwert_…`, D-25). */
export function grundwerte(werte: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(werte)
    .filter(([k]) => k.startsWith("grundwert_"))
    .map(([k, w]) => [k.slice("grundwert_".length), w]));
}

/** Das Zeichen für einen Wert, den die Meldung nicht mitgebracht hat. */
export const FEHLENDER_WERT = "…";

/**
 * Jeder Platzhalter im Satz bekommt einen Wert — den mitgebrachten, sonst
 * `FEHLENDER_WERT`. `satz` ist der Satz **vor** dem Einsetzen (`t(pfad)`).
 */
export function werteMitErsatz(satz: string, werte: Record<string, string>): Record<string, string> {
  const voll: Record<string, string> = { ...werte };
  for (const treffer of satz.matchAll(/\{(\w+)\}/g)) {
    const name = treffer[1];
    if (name !== undefined && !(name in voll)) voll[name] = FEHLENDER_WERT;
  }
  return voll;
}

/**
 * Die Kopfzeile eines Eintrags: Kennung, Kunde, Vorgang — **jeder Teil
 * einmal**, auch wenn die Kennung schon „AB-22 · Kunde" heißt.
 */
export function meldungskopf(d: { kennung?: string | null; kunde?: string | null; vorgang?: string | null }): string {
  const teile: string[] = [];
  const dazu = (wert: string | null | undefined) => {
    for (const teil of (wert ?? "").split(" · ")) {
      const sauber = teil.trim();
      if (sauber && !teile.includes(sauber)) teile.push(sauber);
    }
  };
  dazu(d.kennung);
  dazu(d.kunde);
  dazu(d.vorgang);
  return teile.join(" · ");
}

/* ══════════════ Der Name eines Laufs (M-37, 03.10.2026) ══════════════
   „Hintergrundlauf gescheitert: meldungen" — der technische Name stand in
   der Glocke, und darunter noch einmal. Die Wörter sind dieselben wie unter
   Einstellungen › Jobs (`JOB_ART_TEXT`); hier stehen sie ohne Bezug auf die
   Maske, damit die Glocke sie ohne sie lesen kann. */
export const LAUF_TEXT: Readonly<Record<string, string>> = {
  lex_kunden: "einstellungen.jobsArtLexKunden",
  lex_kunden_schieben: "einstellungen.jobsArtLexKundenSchieben",
  lex_artikel: "einstellungen.jobsArtLexArtikel",
  lex_artikel_schieben: "einstellungen.jobsArtLexArtikelSchieben",
  webhook: "einstellungen.jobsArtWebhook",
  abrechnung: "einstellungen.jobsArtAbrechnung",
  erinnerung: "einstellungen.jobsArtErinnerung",
  optionsverfall: "einstellungen.jobsArtOptionsverfall",
  lex_beleg: "einstellungen.jobsArtLexBeleg",
  lex_zahlungsstand: "einstellungen.jobsArtLexZahlungsstand",
  ressourcenstand: "einstellungen.jobsArtRessourcenstand",
  meldungen: "einstellungen.jobsArtMeldungen",
  archiv: "einstellungen.jobsArtArchiv",
  archiv_pruefung: "einstellungen.jobsArtArchivPruefung",
  lex_verbindung: "einstellungen.jobsArtLexVerbindung",
};

/**
 * Die Werte einer Meldung, mit dem Namen des Laufs statt seines Schlüssels.
 * Nur `lauf_fehler` trägt einen; jede andere Art kommt unverändert zurück.
 */
export function mitLaufname<W extends Record<string, unknown>>(
  art: string, daten: W, t: (pfad: string) => string,
): W {
  if (art !== "lauf_fehler") return daten;
  const roh = typeof daten["lauf"] === "string" ? daten["lauf"] : typeof daten["kennung"] === "string" ? daten["kennung"] : "";
  const pfad = LAUF_TEXT[roh];
  if (!pfad) return daten;
  const name = t(pfad);
  return { ...daten, ...("lauf" in daten ? { lauf: name } : {}), ...("kennung" in daten ? { kennung: name } : {}) };
}
